/**
 * AuditYearSelector — sidebar dropdown for choosing the active audit year.
 *
 * Rendered once inside the Sidebar, just below the SKEPTICS logo.
 * Loads audit years via React Query, pushes them into AuditYearContext,
 * and stores the selection in localStorage.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Lock, CalendarDays } from 'lucide-react';
import { useAuditYears } from '../hooks/useAuditYears.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';

export default function AuditYearSelector() {
  const { data: years, isLoading } = useAuditYears();
  const { selectedAuditYear, selectedAuditYearId, setSelectedAuditYearId, setAuditYears } =
    useAuditYear();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Push freshly loaded years into context.
  useEffect(() => {
    if (years) setAuditYears(years);
  }, [years, setAuditYears]);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (isLoading) {
    return (
      <div className="text-blue-300 text-xs px-1 py-1 animate-pulse">
        Loading years…
      </div>
    );
  }

  if (!years || years.length === 0) {
    return (
      <div className="text-blue-400 text-xs px-1 italic">No audit years yet</div>
    );
  }

  const label = selectedAuditYear
    ? `${selectedAuditYear.name}${selectedAuditYear.is_locked ? ' 🔒' : ''}`
    : 'Select year';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-semibold
                   bg-blue-700 text-blue-100 hover:bg-blue-600 hover:text-white transition-colors
                   border border-blue-500 focus:outline-none focus:ring-2 focus:ring-white"
        title="Switch audit year"
      >
        <div className="flex items-center gap-2 truncate">
          <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-shrink-0 ml-1 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 mt-1 w-56 bg-white rounded-lg shadow-xl py-1 z-50 border border-gray-200">
          <p className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wide">
            Audit Year
          </p>
          {years.map((year) => {
            const isSelected = year.id === selectedAuditYearId;
            return (
              <button
                key={year.id}
                onClick={() => {
                  setSelectedAuditYearId(year.id);
                  setOpen(false);
                }}
                className={`w-full text-left flex items-center justify-between px-3 py-2 text-sm transition-colors
                  ${isSelected
                    ? 'bg-blue-50 text-blue-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span className="truncate">{year.name}</span>
                <span className="flex items-center gap-1 flex-shrink-0 ml-2">
                  {year.is_locked && (
                    <Lock className="h-3 w-3 text-amber-500" title="Locked — historical view" />
                  )}
                  {!year.is_active && (
                    <span className="text-xs text-gray-400">inactive</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
